<div class="setup-step setup-step--wide d-flex flex-column">
  <div class="text-center my-2 setup-step__header">
    <h1 class="mb-1">{{ 'onboarding.setup.planSelection.title' | translate }}</h1>
    <p class="m-0">{{ 'onboarding.setup.planSelection.subtitle' | translate }}</p>
  </div>

  @if (isLoading()) {
  <div
    class="plan-selection__state d-flex flex-column align-items-center justify-content-center text-center py-5 gap-3">
    <mat-spinner diameter="40"></mat-spinner>
    <p class="text-body-secondary mb-0">{{ 'onboarding.setup.planSelection.loading' | translate }}</p>
  </div>
  }

  @if (!isLoading() && errorMessage() && plans().length === 0) {
  <div
    class="plan-selection__state d-flex flex-column align-items-center justify-content-center py-5 gap-3 text-center">
    <mat-icon class="plan-selection__error-icon icon-lg">error_outline</mat-icon>
    <p class="text-body-secondary mb-0">{{ errorMessage() }}</p>
    <button class="av-retry-btn d-inline-flex align-items-center gap-2" (click)="retry()" type="button">
      <mat-icon>refresh</mat-icon>
      {{ 'onboarding.setup.planSelection.tryAgain' | translate }}
    </button>
  </div>
  }

  @if (!isLoading() && plans().length > 0) {

  <div class="d-flex justify-content-center mb-4">
    <div class="offer-toggle">
      <span class="offer-toggle__thumb" [class.offer-toggle__thumb--commit12]="offerType() === 'commit12'"></span>
      <button class="offer-toggle__btn d-inline-flex align-items-center justify-content-center"
        [class.offer-toggle__btn--active]="offerType() === 'monthly'"
        (click)="setOfferType('monthly')" type="button">
        {{ 'onboarding.setup.planSelection.monthly' | translate }}
      </button>
      <button class="offer-toggle__btn d-inline-flex align-items-center justify-content-center"
        [class.offer-toggle__btn--active]="offerType() === 'commit12'"
        (click)="setOfferType('commit12')" type="button">
        {{ 'onboarding.setup.planSelection.commit12' | translate }}
        @if (maxDiscountPercent() !== null) {
        <span class="offer-toggle__pill">{{
          'onboarding.setup.planSelection.discountPill' | translate: { percent: maxDiscountPercent() }
          }}</span>
        }
      </button>
    </div>
  </div>

  @if (errorMessage()) {
  <div class="av-alert av-alert--error d-flex align-items-center gap-2 mb-3">
    <mat-icon>error_outline</mat-icon>
    <span>{{ errorMessage() }}</span>
  </div>
  }

  <div class="plans-grid">
    @for (plan of sortedPlans(); track plan.id) {
    <div class="plan-card position-relative d-flex flex-column"
      [class.plan-card--selected]="selectedPlanId() === plan.id">
      <h2 class="plan-card__name">{{ plan.name }}</h2>

      <p class="plan-card__description">
        {{ getDescriptionKey(plan) | translate }}
      </p>

      @if (offerType() === 'monthly') {
      <div class="plan-card__price plan-card__price--anim d-flex align-items-baseline flex-wrap">
        @if (getSeatPrice(plan); as seatPrice) {
        <span class="plan-card__price-amount">
          {{ getCurrencySymbol(seatPrice.currency) }}{{ formatAmount(seatPrice.unit_amount) }}
        </span>
        <span class="plan-card__price-unit">
          {{ 'onboarding.setup.planSelection.perSeat' | translate }}</span>
        <mat-icon class="plan-card__hint-icon icon-sm flex-shrink-0"
          [matTooltip]="'onboarding.setup.planSelection.tooltips.perSeat' | translate"
          matTooltipPosition="above">info</mat-icon>
        } @else {
        <span class="plan-card__price-amount">—</span>
        }
      </div>
      } @else {
      <div
        class="plan-card__price plan-card__price--anim plan-card__price--commit12 d-flex align-items-baseline flex-wrap">
        @if (getSeatPrice(plan); as seatPrice) {
        @if (getDiscountPercent(plan) !== null) {
        @if (getMonthlySeatPrice(plan); as monthlySeat) {
        <span class="plan-card__price-struck">
          {{ getCurrencySymbol(monthlySeat.currency) }}{{ formatAmount(monthlySeat.unit_amount) }}
        </span>
        }
        }
        <span class="plan-card__price-amount">
          {{ getCurrencySymbol(seatPrice.currency) }}{{ formatAmount(seatPrice.unit_amount) }}
        </span>
        <span class="plan-card__price-unit">
          {{ 'onboarding.setup.planSelection.perSeat' | translate }}</span>
        <mat-icon class="plan-card__hint-icon icon-sm flex-shrink-0"
          [matTooltip]="'onboarding.setup.planSelection.tooltips.perSeat' | translate"
          matTooltipPosition="above">info</mat-icon>
        } @else {
        <span class="plan-card__price-amount">—</span>
        }
      </div>
      @if (getDiscountPercent(plan) !== null) {
      <p class="plan-card__commit-note">{{ 'onboarding.setup.planSelection.commitNote' | translate }}</p>
      }
      }

      @if (getAgentPrice(plan); as agentPrice) {
      <p class="plan-card__base-fee d-flex align-items-center">
        <mat-icon class="plan-card__hint-icon icon-sm flex-shrink-0"
          [matTooltip]="'onboarding.setup.planSelection.tooltips.perAgent' | translate"
          matTooltipPosition="above">info</mat-icon>
        {{
        'onboarding.setup.planSelection.perAgent'
        | translate
        : {
        symbol: getCurrencySymbol(agentPrice.currency),
        amount: formatAmount(agentPrice.unit_amount)
        }
        }}
      </p>
      } @else {
      <p class="plan-card__base-fee plan-card__base-fee--placeholder d-flex align-items-center"></p>
      }

      <button
        class="plan-card__cta plan-card__cta--filled d-flex align-items-center justify-content-center w-100"
        [disabled]="isSubmitting() || selectedPlanId() === plan.id" (click)="selectPlan(plan)" type="button">
        @if (isSubmitting() && selectedPlanId() === plan.id) {
        <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
        } @else if (selectedPlanId() === plan.id && !isSubmitting()) {
        <mat-icon class="icon-sm">check_circle</mat-icon>
        {{ 'onboarding.setup.planSelection.selected' | translate }}
        } @else {
        {{ 'onboarding.setup.planSelection.selectPlan' | translate }}
        }
      </button>

      <div class="my-3"></div>

      <ul class="plan-card__features list-unstyled p-0 m-0 d-flex flex-column flex-1">
        @for (entitlement of getEntitlements(plan); track entitlement.key) {
        @if (isCatalogShowcase(entitlement)) {
        <li class="plan-card__product position-relative d-flex align-items-center">
          <span class="plan-card__product-ribbon">{{
            'onboarding.setup.planSelection.catalogIncluded' | translate
            }}</span>
          <span
            class="plan-card__product-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
            <mat-icon class="icon-md">storefront</mat-icon>
          </span>
          <span class="plan-card__product-text d-flex flex-column">
            <span class="plan-card__product-name">{{ getFeatureLabelKey(entitlement) | translate }}</span>
            <span class="plan-card__product-tagline">{{
              'onboarding.setup.planSelection.catalogTagline' | translate
              }}</span>
          </span>
        </li>
        } @else {
        <li class="plan-card__feature-item d-flex align-items-center flex-wrap">
          <mat-icon class="plan-card__feature-icon icon-sm flex-shrink-0">check</mat-icon>
          <span>{{ getFeatureLabelKey(entitlement) | translate }}</span>
        </li>
        }
        }
      </ul>
    </div>
    }
  </div>
  }
</div>